import React, { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Chat from '.'
import PastChats from './past-chats'
import Modal from '../modal'
import DropdownPanel from '../dropdown-panel'
import NoteInfo from '../note-info'
import CustomAnchor from '../custom-anchor'
import { deleteChatHistoryAPI, getPastChatsAPI, isCanceledRequestError } from '../../common/services'
import { useToaster } from '../../common/contexts/toast'

const SEARCH_MIN_CHARS = 3

const ChatWithPastChat = ({ title, children, houseId, objectId, onAskQuestion, onResetChat, onPastChatClick, type: chatType = "organization", disabled = false, onChatStartedChange, notes = [], docFilters, onDocFiltersChange, suggestedQuestions, ...otherChatProps }) => {

    // local states
    const [chatId, setChatId] = useState()
    const [isChatStarted, setIsChatStarted] = useState(false)
    const [pastChats, setPastChats] = useState([])
    const [pastChatLoading, setPastChatLoading] = useState(false)
    const [disabledChat, setDisabledChat] = useState(false)
    const [isPastChatsOpen, setIsPastChatsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')

    //-- modal local states
    const [commonModalData, setCommonModalData] = useState({ title: "", type: "", size: "" })
    const [isModalVisible, setIsModalVisible] = useState(false);
    //-- end

    // refs
    const pastChatsRequestSeqRef = useRef(0)
    const chatRef = useRef(null)

    const onSuggestedQuestionClick = useCallback((question) => {
        chatRef.current?.askQuestion(question)
    }, [])

    // close dropdown when clicking outside the wrapper (input + dropdown)
    const closePastChatsDropdown = useCallback(() => setIsPastChatsOpen(false), [])

    // hook
    const { showToaster } = useToaster()

    useEffect(() => {
        onChatStartedChange && onChatStartedChange(isChatStarted)
    }, [isChatStarted, onChatStartedChange])

    // when clicked on any past chat
    const onClickPastChat = useCallback((pastChatData) => {
        const pastChatId = pastChatData.chatId
        setChatId(pastChatId)
        setIsChatStarted(true)
        setIsPastChatsOpen(false)
        onPastChatClick && onPastChatClick(pastChatData)
    }, [onPastChatClick])

    // open dropdown when input is focused (only relevant before chat starts)
    const onAskInputFocus = useCallback(() => {
        setIsPastChatsOpen(true)
    }, [])

    // track input value for client-side past-chat filtering
    const onAskInputChange = useCallback((value) => {
        setSearchQuery(value || '')
    }, [])

    // client-side filter: substring match on title once user types >= SEARCH_MIN_CHARS
    const filteredPastChats = useMemo(() => {
        const query = (searchQuery || '').trim().toLowerCase()
        if (query.length < SEARCH_MIN_CHARS) return pastChats
        return pastChats.filter((chat) => (chat?.title || '').toLowerCase().includes(query))
    }, [pastChats, searchQuery])

    // when ask question is clicked
    const handleAskQuestion = useCallback((question) => {
        setIsChatStarted(true)
        onAskQuestion && onAskQuestion(question)
    }, [onAskQuestion])

    const placeholderText = useMemo(() => {
        if (objectId) {
            return "Ask a question"
        } else if (isChatStarted) {
            return "Ask a question"
        } else {
            return "Ask a question"
        }
    }, [objectId, isChatStarted])

    // Past chat functions
    const getAllPastChatsList = useCallback(async (objectId, docFilters) => {
        const mySeq = ++pastChatsRequestSeqRef.current;
        const isLatest = () => mySeq === pastChatsRequestSeqRef.current;

        try {
            setPastChatLoading(true)

            let pastChatAPIData = {
                type: chatType
            }

            if (houseId) {
                pastChatAPIData.houseId = houseId
            }

            if (objectId) {
                pastChatAPIData.objectId = objectId?.toString()
            }

            if (docFilters) {
                pastChatAPIData.docFilters = docFilters;
            }

            const pastChatRes = await getPastChatsAPI(pastChatAPIData)
            // A newer request has already been kicked off — drop this stale response.
            if (!isLatest()) return;
            const allPastchats = pastChatRes.data.chats;
            setPastChats(allPastchats)
            setPastChatLoading(false)
        } catch (error) {
            console.log("Error while fetching past chats", error);
            if (isLatest() && !isCanceledRequestError(error)) {
                setPastChatLoading(false)
            }
        }
    }, [houseId, chatType])

    // when reset chat is clicked
    const handleResetChat = useCallback(({ resetPastHistory = true } = {}) => {
        setIsChatStarted(false)
        setChatId(null)
        onResetChat && onResetChat()
    }, [onResetChat])    

    // calling api on intial load + on objectId change + on reset.
    useEffect(() => {
        if (!isChatStarted) {
            getAllPastChatsList(objectId, docFilters)
        }
    }, [getAllPastChatsList, objectId, isChatStarted, docFilters])

    const onDeletePastChat = useCallback((chatData) => {
        setCommonModalData({
            title: "Delete chat",
            size: "medium",
            type: "delete-chat",
            confirmButtonName: "DELETE",
            cancelButtonName: "CANCEL",
            message: <>{"You are about to delete the chat "}<span className='chat-title-text'>{`${chatData?.title || ''}`} </span> <br/> <br/> {"Note, this chat is currently available for your organization team on Bimmatch."} </>,
            chatData,
        });
        setIsModalVisible(true);
    }, [])

    const onClickConfirmModal = useCallback(async (type) => {

        // deleting selected chat
        if (type === "delete-chat") {
            try {
                const toDeleteChatId = commonModalData.chatData.chatId
                setCommonModalData(oldModalData => ({ ...oldModalData, confirmButtonLoading: true }))
                
                const deleteRes = await deleteChatHistoryAPI({ chatId: toDeleteChatId })

                setPastChats(pastChats => pastChats.filter((pastChat) => pastChat.chatId !== toDeleteChatId ))

                // if chat has been deleted from open chat screen then reset the data
                if (isChatStarted) {
                    commonModalData.chatData.resetChat()
                }
                showToaster(`chat-toast`, deleteRes?.data?.message, true)
            } catch (error) {
                // Backend error envelope changed in the docs REST migration:
                //   new: { error: { code, message } }
                //   old: { message }
                // Read the new shape first, fall back to legacy for safety.
                const apiMessage =
                    error?.response?.data?.error?.message ||
                    error?.response?.data?.message
                showToaster(`chat-toast`, apiMessage || "Something went wrong", false)
                console.log("Error while deleting Chat", error)
            } finally {
                setCommonModalData({})
                setIsModalVisible(false)
            }
        }

    }, [commonModalData, isChatStarted, showToaster])

    // here reset chat is function from inside chat comp passed as arg to clear inside data too
    const onDeleteChat = useCallback((resetChat, newlyCreatedChatId) => {
        onDeletePastChat({ chatId : (chatId || newlyCreatedChatId), resetChat })
    }, [onDeletePastChat, chatId])

    const onFetchDocsSuccess = useCallback((docs) => {
        setDisabledChat(!docs?.length)
    }, [])   

    return (
        <>
            {(!isChatStarted && !objectId && title) && <p className='ask-title'>{title}</p>}
                <DropdownPanel
                    isOpen={!isChatStarted && isPastChatsOpen}
                    onClose={closePastChatsDropdown}
                    content={(
                        <PastChats
                            objectId={objectId}
                            houseId={houseId}
                            onClickChat={onClickPastChat}
                            type={chatType}
                            loading={pastChatLoading}
                            onClickDelete={onDeletePastChat}
                            data={filteredPastChats}
                        />
                    )}
                    className="past-chats-dropdown"
                >
                    <Chat
                        ref={chatRef}
                        id={chatId}
                        objectId={objectId}
                        houseId={houseId}
                        onAskQuestion={handleAskQuestion}
                        onResetChat={handleResetChat}
                        type={chatType}
                        onClickDelete={onDeleteChat}
                        disabled={disabled || disabledChat}
                        placeholder={(disabled || disabledChat) ? 'Ask a question' : placeholderText}
                        docFilters={docFilters}
                        onDocFiltersChange={onDocFiltersChange}
                        onInputFocus={onAskInputFocus}
                        onInputChange={onAskInputChange}
                        {...otherChatProps}
                    />
                </DropdownPanel>
            {(!isChatStarted && notes.length > 0) && (
                <div className="notes-wrapper">
                    {notes.map((note, index) => (
                        <NoteInfo {...(typeof note === "string" ? { note } : note)} withInfoIcon key={index} />
                    ))}
                </div>
            )}
            {!isChatStarted && !!suggestedQuestions?.length && (
                <div className='suggested-questions-wrapper'>
                    <p className='suggested-questions-title'>Suggested questions</p>
                    <div className='suggested-questions-list'>
                        {suggestedQuestions.map((question, index) => (
                            <CustomAnchor
                                key={index}
                                onClick={() => onSuggestedQuestionClick(question)}
                                disabled={disabled || disabledChat}
                            >
                                {question}
                            </CustomAnchor>
                        ))}
                    </div>
                    <hr className='separator' />
                </div>
            )}

            {/* Note: Currently Docs list comes here and only visible when no chat has started */}
            {children ? cloneElement(children, { isChatStarted, chatId, onFetchDocsSuccess }) : <></>}

            <Modal
                size={"medium"}
                visible={isModalVisible}
                title={commonModalData?.title}
                modalClass={commonModalData?.type}
                confirmButtonName={commonModalData?.confirmButtonName}
                cancelButtonName={commonModalData?.cancelButtonName}
                onClickCancel={() => setIsModalVisible(false)}
                onClickConfirm={() => onClickConfirmModal(commonModalData.type)}
                onClose={() => setIsModalVisible(false)}
                extraMessage={commonModalData?.message}
                confirmButtonLoading={commonModalData?.confirmButtonLoading}
                confirmButtonDisable={commonModalData?.confirmButtonLoading}
                type={commonModalData?.modalContentType || "surprised"}
            />
        </>
    )
}

export default ChatWithPastChat